import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { RIDE_STATUSES, RING_STATUS, TERM_REASONS } from "score-common";
import { useAnnouncerContext } from "../context/AnnouncerContext";
import { useRingData } from "../context/RingDataContext";
import {
  selectLiveDeskSequence,
  findRideResult,
  isRideResultPending,
  describeJustFinishedMode,
  describeJustFinishedPlacing,
  JUST_FINISHED_MODE,
} from "../utils/liveDesk";
import { formatTime } from "../utils/formatTime";
import { formatOrdinal } from "../utils/formatOrdinal";
import { formatComposite } from "../utils/formatComposite";
import { formatElapsedSeconds } from "../utils/presence";

// #2149 (docs/score/announcer-ui-redesign.md §4.3) — band 3, the live desk.
// Three FIXED slots (JUST FINISHED / IN THE RING NOW / NEXT UP) at fixed
// heights, at every state of the data (Rule 1 + Rule 3). Consumes ONLY
// useAnnouncerContext()/useRingData() — no new hook, no new poll (#2137's
// whole point, gotcha 2). Slot 1's score comes PASSIVELY off
// `resultsByClassId`, which is lifted from whichever ClassResults instance
// mounted that class (later cards #2141/#2142) — it stays empty until then
// by design, not a bug, so Slot 1 correctly reads "score pending" until a
// look-up band exists to populate it.
//
// No "entered ring" timestamp exists anywhere in the codebase distinct from
// `ride.scheduledTime` (confirmed during design research, #2149 handoff) —
// so `entered {{time}}` is `formatTime(currentRow.scheduledTime)` and
// `in the ring {{elapsed}}` is `liveNow - scheduledTime` via the existing
// `formatElapsedSeconds` (utils/presence.js). This is a substitution, not
// the literal ring-entry event, documented here rather than re-litigated.
//
// Likewise there is no "ring resumes at" field on a ring-state BREAK row
// (score-common's RingState schema carries no such value) — `back at` for
// the ON BREAK state line is read off the first NEXT UP row's own
// scheduledTime (the next ride still due once the ring picks back up),
// which is real data already on the row, not invented. Same design-decision
// category as the entered-ring substitution above, not a federation-rule
// question.

const NEXT_UP_COUNT = 3;
const SLOT1_HEIGHT = 176;
const SLOT2_HEIGHT = 300;
const NEXT_UP_ROW_HEIGHT = 32;
const NEXT_UP_HEADER_HEIGHT = 28;

// AC13 (bug 144, Evin) — the announcer may be watching more than one ring on
// a small screen, which in the booth means a SHORT browser window (two
// windows stacked to watch two rings), not a narrow one — the desk's width
// is already pinned at LIVE_DESK_WIDTH regardless of viewport. At the normal
// sizes above, the three slots need 176+300+124=600px of vertical room.
// AnnouncerFrame's `overflow:"hidden"` on the live-desk band silently clips
// the bottom of NEXT UP off-screen when that room isn't there — invisible,
// not just tight — since flexShrink:0 on every slot lets the column run
// past its parent's real height without complaint.
//
// #2145 — this used to be a `useMediaQuery("(max-height:640px)")` guess,
// computed from window height minus AppBar/AppFooter's two FIXED chrome
// heights alone. That broke the moment this card re-homed StatusBar (#2144)
// into AnnouncerFrame's normal flow above the band row (AnnouncerFrame.jsx):
// StatusBar's own height is real but VARIABLE (it wraps at a small viewport,
// #1875's same trap the old sticky header had), so no window-height
// threshold can predict how much of the window StatusBar actually eats.
// The band silently lost real height to it and the fixed window-height
// guess never noticed (AC1/AC13 caught it: NEXT UP's last row rendered
// behind AppFooter even at Cypress's own default 660px-tall viewport).
// Measuring the band's OWN rendered height (ResizeObserver, same idiom
// DaySpine.jsx's own measure-then-compress ladder already uses) is correct
// regardless of what else is stacked above it, now or in the future.
const NORMAL_TOTAL_HEIGHT = SLOT1_HEIGHT + SLOT2_HEIGHT + NEXT_UP_HEADER_HEIGHT + NEXT_UP_COUNT * NEXT_UP_ROW_HEIGHT;
const SLOT1_HEIGHT_COMPACT = 140;
const SLOT2_HEIGHT_COMPACT = 220;
const NEXT_UP_ROW_HEIGHT_COMPACT = 24;
// NEXT UP's Box has `py: 1` (8px top + 8px bottom) the header+row sum was
// never accounting for -- normal mode has enough slack that it goes
// unnoticed (proven fine by the existing AC1/AC5 tests, left untouched
// here), but compact's tighter budget makes a real 16px difference: without
// it the last row was clipped by the box's OWN overflow:hidden even though
// its outer-frame position looked fine (caught by screenshot inspection,
// not the outer-frame-only bounding check alone). Folded into this constant
// alone, not the shared formula, so normal mode's already-verified geometry
// (AC1's "next-up be.visible" — moving normal's total column height even
// 16px taller pushes it past the ambient-viewport frame bottom) is untouched.
const NEXT_UP_HEADER_HEIGHT_COMPACT = 40;
const COMPACT_TOTAL_HEIGHT =
  SLOT1_HEIGHT_COMPACT + SLOT2_HEIGHT_COMPACT + NEXT_UP_HEADER_HEIGHT_COMPACT + NEXT_UP_COUNT * NEXT_UP_ROW_HEIGHT_COMPACT;

// #2447 (bug 144, Evin, https://trello.com/c/zI3rxeKZ) -- a THIRD tier below
// compact. Compact's own total (472px: 140+220+112) still doesn't fit the
// booth's shortest stacked-window case (AC13, 1280x600), where the band's
// real measured available height is only ~433px -- and an even shorter
// 1000x560 booth window measures only ~393px available. Same
// ResizeObserver-driven measurement as the normal/compact switch above (not
// a second media-query guess): SMALL activates once the band's OWN rendered
// height drops below COMPACT_TOTAL_HEIGHT, exactly the same "measure what we
// actually have" idiom, just one rung lower.
//
// These numbers were tuned against AC13's own internal-clip assertions
// (both boxes' `overflow:hidden`, not just the outer frame) at BOTH target
// viewports, not derived from font-metrics arithmetic alone. Two things had
// to give beyond just three smaller pixel presets, recorded here so the next
// tuning pass does not repeat the same dead ends:
//   1. The three boxes' own `py` padding (slotPy/nextUpPy below) shrinks
//      further at SMALL than at compact -- the pixel-preset numbers alone
//      were never going to fit 1000x560's ~393px floor, since each box's
//      real content (title text, the entered/elapsed line, etc.) does not
//      shrink just because the tier's height budget did; only padding is
//      "free" room to reclaim. Slot1/Slot2 go to `py: 0.5` (compact/normal
//      use 1.5), NEXT UP to `py: 0.5` (compact uses 1).
//   2. NEXT_UP_ROW_HEIGHT_SMALL had to drop to 17 (below compact's 24) to
//      make the 1000x560 budget close at all -- the smallest of these four
//      numbers, and the one with the least further room if a shorter
//      viewport than 1000x560 ever needs supporting.
// Verified against BOTH measured floors: 92+210+37+51=390px, a 3px margin
// under 393px (1000x560) and a much larger 43px margin under 433px
// (1280x600) -- deliberately tight at the shorter floor rather than
// generous at the taller one, since 1000x560 is the harder case in scope.
const SLOT1_HEIGHT_SMALL = 92;
const SLOT2_HEIGHT_SMALL = 210;
const NEXT_UP_ROW_HEIGHT_SMALL = 17;
const NEXT_UP_HEADER_HEIGHT_SMALL = 37;

// #2471 -- SMALL is one fixed pixel preset (not fluid, not media-query-
// scaled), so there is no further tier below it: 1000x560 (~393px available)
// is the currently DOCUMENTED supported floor, with only 3px of margin, and
// this is the arithmetic answer #2447 shipped, not a measured one (its own
// description left that as an open question). A real shorter booth window
// clips again below this floor with nothing in the UI indicating it -- that
// requires either a real measurement of an actual booth's shortest stacked-
// window height, or an explicit product call taking 1000x560 as the floor on
// purpose, neither of which this card's own arithmetic can settle (#2471
// AC2/AC3; tracked on the card, not resolved here).
function exceptionStatusLabel(row, t) {
  if (!row) return null;
  // AC11 precedence: ELIMINATED wins over whatever raw termReason string
  // would otherwise show (#1838 precedent) — score-common's TERM_REASONS,
  // USEF DR123.3 vocabulary (docs/rules/usef/rulebook-dr-dressage-2026.pdf,
  // #2052/#2053).
  if (row.termReason === TERM_REASONS.ELIMINATED) return t("liveDesk.eliminated");
  if (row.status === RIDE_STATUSES.SCRATCHED) return row.termReason || t("oog.scratched");
  // An ordinary unplaced ride shows NO status text (AC11).
  return null;
}

function PlacingText({ result, t, tier }) {
  const mode = describeJustFinishedMode(result);
  if (mode === JUST_FINISHED_MODE.FINAL) {
    // AC12 — result.placing === 0 is STORED DATA meaning UNPLACED, never an
    // index to add 1 to. describeJustFinishedPlacing owns that decision.
    const { unplaced, placing } = describeJustFinishedPlacing(result);
    const placingText = unplaced ? t("results.unplacedLabel") : formatOrdinal(placing);
    // #2452 -- at normal size h6 fits fine, but slot 1's fixed height budget
    // (tuned for score+metadata alone, #2145) never reserved room for a
    // THIRD line once #2447 added the compact/small tiers on top of it, and
    // this line's own overflow:hidden then cut it mid-glyph against IN THE
    // RING NOW below. Shrinking the placing line's own typography at the
    // tighter tiers (same idiom InTheRingSlot already uses for its h4/h2
    // competitor-number swap, not a media query) is the fix in scope,
    // rather than growing SLOT1_HEIGHT, which AC1/AC13 already pin against
    // the ambient-viewport frame bottom for every tier.
    const variant = tier === "small" ? "caption" : tier === "compact" ? "body2" : "h6";
    const sx = tier === "small" ? { color: "text.primary", fontWeight: 700 } : { color: "text.primary" };
    return (
      <Typography data-testid="live-desk-just-finished-placing" variant={variant} sx={sx}>
        {placingText}
      </Typography>
    );
  }
  if (mode === JUST_FINISHED_MODE.CONFIRMED) {
    // AC10 — checkmark PLUS the literal word "Confirmed", never colour alone.
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        data-testid="live-desk-just-finished-confirmed"
      >
        <CheckCircleIcon fontSize="small" sx={{ color: colors.foxOrange }} />
        <Typography variant="body2" sx={{ color: "text.primary" }}>
          {t("liveDesk.confirmed")}
        </Typography>
      </Stack>
    );
  }
  return null;
}

function JustFinishedSlot({ row, resultsByClassId, height, py, tier, t }) {
  const result = row ? findRideResult(resultsByClassId, row) : null;
  const pending = row ? isRideResultPending(result) : true;
  const exceptionLabel = exceptionStatusLabel(row, t);
  const struck = row?.status === RIDE_STATUSES.SCRATCHED ? { textDecoration: "line-through" } : null;

  return (
    <Box
      data-testid="live-desk-just-finished"
      sx={{
        height,
        flexShrink: 0,
        overflow: "hidden",
        px: 2,
        py,
        textAlign: "right",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      {/* #2452 -- overline's own default lineHeight (2.66) reserves far more
          vertical room than its 12px text needs; tightening it at the two
          compressed tiers reclaims real room for the score/placing lines
          below without touching SLOT1_HEIGHT itself. */}
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1, lineHeight: tier === "normal" ? undefined : 1.3 }}
      >
        {t("liveDesk.justFinishedTitle")}
      </Typography>
      {!row ? (
        <Typography variant="body2" color="text.secondary">
          {t("liveDesk.empty")}
        </Typography>
      ) : (
        <Stack spacing={0.25} alignItems="flex-end">
          <Typography variant="body2" color="text.secondary" sx={struck}>
            {[row.competitorNumber, row.horseName, row.riderName].filter(Boolean).join(" · ")}
          </Typography>
          {exceptionLabel ? (
            <Typography data-testid="live-desk-just-finished-status" variant="body2" sx={{ fontWeight: 700 }}>
              {exceptionLabel}
            </Typography>
          ) : pending ? (
            <Box
              data-testid="live-desk-score-pending"
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexGrow: 1 }}
            >
              <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 700 }}>
                {t("liveDesk.scorePending")}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={tier === "small" ? 0 : 0.5} alignItems="flex-end">
              {/* AC9 — plain TEXT, no chiclet/bubble; score visually dominant
                  over the rider-metadata line above (folded from #2098,
                  bug 144). #2452 -- the score itself shrinks at the tighter
                  tiers too (same reasoning as PlacingText below: three lines
                  of content now share slot 1's fixed height budget, at every
                  tier, not just normal). */}
              <Typography
                data-testid="live-desk-just-finished-score"
                variant={tier === "small" ? "h6" : tier === "compact" ? "h4" : "h3"}
                sx={{ fontWeight: 800, color: "text.primary", lineHeight: 1.1 }}
              >
                {formatComposite(result?.composite) ? `${formatComposite(result.composite)}%` : "—"}
              </Typography>
              <PlacingText result={result} t={t} tier={tier} />
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
}

function InTheRingSlot({ currentRow, ringState, selectedRing, liveNow, nextUpRows, height, py, compact, t }) {
  const stateLine = useMemo(() => {
    if (currentRow) return null;
    if (ringState?.status === RING_STATUS.BREAK) {
      const backAt = nextUpRows?.[0]?.scheduledTime ? formatTime(nextUpRows[0].scheduledTime) : null;
      return backAt ? t("liveDesk.onBreakUntil", { time: backAt }) : t("liveDesk.onBreak");
    }
    if (ringState?.status === RING_STATUS.END_OF_DAY) {
      return t("liveDesk.ringDone", { ring: ringState?.ring ?? selectedRing ?? "" });
    }
    return t("liveDesk.noClassInProgress");
  }, [currentRow, ringState, nextUpRows, selectedRing, t]);

  const judgesText = (currentRow?.judges || [])
    .map((j) => t("liveDesk.judgeAtPosition", { position: j.position, name: j.name }))
    .filter(Boolean)
    .join(", ");

  const elapsedSeconds = currentRow?.scheduledTime
    ? Math.max(0, (liveNow - Date.parse(currentRow.scheduledTime)) / 1000)
    : null;

  const classLine = currentRow
    ? [currentRow.classNumber, currentRow.className].filter(Boolean).join(" ")
    : "";

  return (
    <Box
      data-testid="live-desk-in-the-ring"
      sx={{
        height,
        flexShrink: 0,
        overflow: "hidden",
        px: 2,
        py,
        bgcolor: "action.hover",
        borderLeft: `6px solid ${colors.foxOrange}`,
        borderBottom: 1,
        // `borderColor` (all four sides) must NOT follow `borderLeft` here: it would
        // overwrite the left border's colour with the divider grey, leaving the "in
        // the ring" accent bar visually indistinguishable from an ordinary divider.
        // `borderBottomColor` touches only the bottom edge it's meant for (#2146).
        borderBottomColor: "divider",
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
        {t("liveDesk.inTheRingTitle")}
      </Typography>
      {!currentRow ? (
        <Box sx={{ display: "flex", alignItems: "center", height: "80%" }}>
          <Typography data-testid="live-desk-ring-state" variant="h6" sx={{ fontWeight: 700 }}>
            {stateLine}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={0.5}>
          <Typography
            data-testid="live-desk-current-competitor-number"
            variant={compact ? "h4" : "h2"}
            sx={{ fontWeight: 800, lineHeight: 1.05, color: "text.primary" }}
          >
            {currentRow.competitorNumber}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {currentRow.horseName}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentRow.riderName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {classLine}
          </Typography>
          {judgesText ? (
            <Typography data-testid="live-desk-current-judges" variant="body2" color="text.secondary">
              {judgesText}
            </Typography>
          ) : null}
          {currentRow.pedigree ? (
            <Typography variant="caption" color="text.secondary">
              {currentRow.pedigree}
            </Typography>
          ) : null}
          {currentRow.ownerLine ? (
            <Typography variant="caption" color="text.secondary">
              {currentRow.ownerLine}
            </Typography>
          ) : null}
          <Typography data-testid="live-desk-current-times" variant="body2" color="text.secondary">
            {[
              currentRow.scheduledTime ? t("liveDesk.entered", { time: formatTime(currentRow.scheduledTime) }) : null,
              elapsedSeconds != null
                ? t("liveDesk.inTheRingElapsed", { elapsed: formatElapsedSeconds(elapsedSeconds) })
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

function NextUpRow({ row, index, rowHeight, t }) {
  const isScratched = row?.status === RIDE_STATUSES.SCRATCHED;
  const struck = isScratched ? { textDecoration: "line-through" } : null;
  const exceptionLabel = exceptionStatusLabel(row, t);

  return (
    <Stack
      key={row?.id ?? `empty-${index}`}
      data-testid={`live-desk-next-up-row-${index}`}
      data-different-class={row ? String(Boolean(row.differentClass)) : undefined}
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ height: rowHeight, flexShrink: 0, overflow: "hidden", ...struck }}
    >
      {row ? (
        <>
          <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 32, ...struck }}>
            {row.competitorNumber}
          </Typography>
          <Typography variant="body2" sx={{ flexGrow: 1, ...struck }} noWrap>
            {[row.horseName, row.riderName].filter(Boolean).join(" / ")}
          </Typography>
          {row.differentClass ? (
            <Typography
              data-testid={`live-desk-next-up-row-${index}-class`}
              variant="caption"
              color="text.secondary"
            >
              {[row.classNumber, row.className].filter(Boolean).join(" ")}
            </Typography>
          ) : null}
          {exceptionLabel ? (
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {exceptionLabel}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            {formatTime(row.scheduledTime)}
          </Typography>
        </>
      ) : null}
    </Stack>
  );
}

function NextUpSlot({ nextUpRows, rowHeight, headerHeight, py, t }) {
  // Rule 1 — exactly NEXT_UP_COUNT rows, ALWAYS, whether or not the data
  // fills them (AC2/AC5: fixed row count, does not grow to fit).
  const slots = Array.from({ length: NEXT_UP_COUNT }, (_, i) => nextUpRows[i] ?? null);
  return (
    <Box
      data-testid="live-desk-next-up"
      sx={{
        height: headerHeight + NEXT_UP_COUNT * rowHeight,
        flexShrink: 0,
        overflow: "hidden",
        px: 2,
        py,
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
        {t("liveDesk.nextUpTitle")}
      </Typography>
      <Stack spacing={0}>
        {slots.map((row, i) => (
          <NextUpRow key={row?.id ?? `empty-${i}`} row={row} index={i} rowHeight={rowHeight} t={t} />
        ))}
      </Stack>
    </Box>
  );
}

function LiveDesk() {
  const { t } = useTranslation("announcer");
  const { selectedRing } = useAnnouncerContext();
  const { rows, currentRideId, resultsByClassId, ringState, liveNow } = useRingData();
  // AC13 — see NORMAL_TOTAL_HEIGHT comment above: measure the band's OWN
  // rendered height rather than guessing from window height, so whatever
  // else is stacked above it (StatusBar today, anything else later) is
  // automatically accounted for. Starts `null` (treated as "fits normally")
  // before the first measurement lands, same as DaySpine's own ladder, so a
  // fixture that fits never flashes compressed.
  const containerRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entriesList) => {
      const entry = entriesList[0];
      if (entry) setAvailableHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const compact = availableHeight != null && availableHeight < NORMAL_TOTAL_HEIGHT;
  // #2447 -- SMALL is strictly a subset of compact (COMPACT_TOTAL_HEIGHT <
  // NORMAL_TOTAL_HEIGHT), so `compact` alone still gates the h4/h2 typography
  // swap in InTheRingSlot below; SMALL only narrows which pixel preset the
  // three slots use.
  const small = availableHeight != null && availableHeight < COMPACT_TOTAL_HEIGHT;
  const slot1Height = small ? SLOT1_HEIGHT_SMALL : compact ? SLOT1_HEIGHT_COMPACT : SLOT1_HEIGHT;
  const slot2Height = small ? SLOT2_HEIGHT_SMALL : compact ? SLOT2_HEIGHT_COMPACT : SLOT2_HEIGHT;
  const nextUpRowHeight = small ? NEXT_UP_ROW_HEIGHT_SMALL : compact ? NEXT_UP_ROW_HEIGHT_COMPACT : NEXT_UP_ROW_HEIGHT;
  const nextUpHeaderHeight = small
    ? NEXT_UP_HEADER_HEIGHT_SMALL
    : compact
      ? NEXT_UP_HEADER_HEIGHT_COMPACT
      : NEXT_UP_HEADER_HEIGHT;
  // #2447 -- SMALL also trims each box's own `py` padding (not just the
  // height presets above): at the shortest real booth viewport in scope
  // (1000x560, ~393px available), compact's own padding (24px on Slot1/
  // Slot2, 16px on NEXT UP) alone left no room even at every box's own
  // content floor, since content itself doesn't shrink with the tier -- only
  // padding is reclaimable. Both go to `0.5` (8px each) here, proportionally
  // smaller rather than eliminated, so the visual rhythm between boxes
  // survives at this tier too.
  const slotPy = small ? 0.5 : 1.5;
  const nextUpPy = small ? 0.5 : 1;
  // #2452 -- JustFinishedSlot's own score/placing typography needs all three
  // tiers, not just the compact/normal split InTheRingSlot's h4/h2 swap
  // uses, so it gets a dedicated string rather than reusing `compact` alone.
  const justFinishedTier = small ? "small" : compact ? "compact" : "normal";

  const { currentRow, justFinishedRow, nextUpRows } = useMemo(
    () => selectLiveDeskSequence({ rows, currentRideId, nextUpCount: NEXT_UP_COUNT }),
    [rows, currentRideId],
  );

  return (
    <Box
      ref={containerRef}
      data-testid="live-desk"
      sx={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}
    >
      <JustFinishedSlot
        row={justFinishedRow}
        resultsByClassId={resultsByClassId}
        height={slot1Height}
        py={slotPy}
        tier={justFinishedTier}
        t={t}
      />
      <InTheRingSlot
        currentRow={currentRow}
        ringState={ringState}
        selectedRing={selectedRing}
        liveNow={liveNow}
        nextUpRows={nextUpRows}
        height={slot2Height}
        py={slotPy}
        compact={compact}
        t={t}
      />
      <NextUpSlot
        nextUpRows={nextUpRows}
        rowHeight={nextUpRowHeight}
        headerHeight={nextUpHeaderHeight}
        py={nextUpPy}
        t={t}
      />
    </Box>
  );
}

export default LiveDesk;
